import React, { useEffect, useState } from 'react';
import { ErrorMessage, Formik } from 'formik';
import Swal from "sweetalert2";
import { useNavigate, useParams } from 'react-router-dom';
import * as emailTemplateService from '../../../services/email.template';
import Breadcrums from '../../../common/breadcrumbs';
import CustomCkeditor from '../../../common/customeditor';
import { EMAIL_TEMPLATE_TYPES, SWAL_SETTINGS } from '../../../../../utils/Constants';
import { globalLoader, handleServerValidations } from '../../../../../utils/commonfunction';
import * as Yup from 'yup';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'gb' },
  { code: 'de', name: 'German', flag: 'de' },
  { code: 'hu', name: 'Hungarian', flag: 'hu' },
  { code: 'es', name: 'Spanish', flag: 'es' },
];

const EmailTempEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [initialData, setInitialData] = useState(null);

  const breadcrumbs = [
    { title: "Dashboard", url: "/dashboard" },
    { title: "Automatic Email", url: "/admin/cms/default-email-template/list/1" },
    { title: "Edit", url: "" },
  ];

  useEffect(() => {
    globalLoader(true);
    emailTemplateService.Details(id)
      .then(response => {
        if (response?.success && response?.data) {
          setInitialData(response.data);
        } else {
          Swal.fire({ icon: 'error', text: "Template not found", ...SWAL_SETTINGS });
          navigate('/admin/cms/default-email-template/list/1');
        }
      })
      .catch(err => {
        console.error("Load error:", err);
        Swal.fire({ icon: 'error', text: "Failed to load template", ...SWAL_SETTINGS });
        navigate(-1);
      })
      .finally(() => globalLoader(false));
  }, [id, navigate]);

  const validationSchema = Yup.object().shape({
    slug: Yup.string()
      .required("Slug is required")
      .matches(/^[a-z0-9-_]+$/, "Only lowercase letters, numbers, hyphens, and underscores allowed"),

    title_en: Yup.string().required("English title is required").test('trim', 'Required', v => v?.trim()),
    title_de: Yup.string().required("German title is required").test('trim', 'Required', v => v?.trim()),
    title_hu: Yup.string().required("Hungarian title is required").test('trim', 'Required', v => v?.trim()),
    title_es: Yup.string().required("Spanish title is required").test('trim', 'Required', v => v?.trim()),

    subject_en: Yup.string().required("English subject is required"),
    subject_de: Yup.string().required("German subject is required"),
    subject_hu: Yup.string().required("Hungarian subject is required"),
    subject_es: Yup.string().required("Spanish subject is required"),

    description_en: Yup.string().required("English description is required").min(10, "Too short"),
    description_de: Yup.string().required("German description is required").min(10, "Too short"),
    description_hu: Yup.string().required("Hungarian description is required").min(10, "Too short"),
    description_es: Yup.string().required("Spanish description is required").min(10, "Too short"),
  });

  if (!initialData) {
    return (
      <>
        <Breadcrums data={breadcrumbs} />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  const initialValues = {
    slug: initialData.slug || "",
    title_en: initialData.title?.en || "",
    title_de: initialData.title?.de || "",
    title_hu: initialData.title?.hu || "",
    title_es: initialData.title?.es || "",
    subject_en: initialData.subject?.en || "",
    subject_de: initialData.subject?.de || "",
    subject_hu: initialData.subject?.hu || "",
    subject_es: initialData.subject?.es || "",
    description_en: initialData.description?.en || "",
    description_de: initialData.description?.de || "",
    description_hu: initialData.description?.hu || "",
    description_es: initialData.description?.es || "",
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitted(true);
          globalLoader(true);

          const formData = new FormData();
          formData.append('o_id', id);
          formData.append('slug', values.slug);

          LANGUAGES.forEach(lang => {
            formData.append(`title_${lang.code}`, values[`title_${lang.code}`]);
            formData.append(`subject_${lang.code}`, values[`subject_${lang.code}`]);
            formData.append(`description_${lang.code}`, values[`description_${lang.code}`]);
          });

          emailTemplateService.Edit(formData)
            .then(response => {
              if (response.success) {
                Swal.fire({ icon: 'success', text: response.message || "Updated successfully!", ...SWAL_SETTINGS });
                setTimeout(() => navigate('/admin/cms/default-email-template/list/1'), 2000);
              } else {
                Swal.fire({ icon: 'error', text: handleServerValidations(response), ...SWAL_SETTINGS });
                setSubmitted(false);
              }
            })
            .catch(err => {
              console.error(err);
              Swal.fire({ icon: 'error', text: "Update failed!", ...SWAL_SETTINGS });
              setSubmitted(false);
            })
            .finally(() => {
              setSubmitting(false);
              globalLoader(false);
            });
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <div className="row row-sm">
              <div className="col-lg-12 col-md-12 animation_fade">
                <div className="card custom-card">
                  <div className="card-body">
                    <h6 className="main-content-label mb-3">Edit Automatic Email (4 Languages)</h6>

                    {/* Slug */}
                    <div className="row row-sm mb-3">
                      <div className="col-md-12 form-group">
                        <label className="text-left d-flex">Slug: <span className="requirestar">*</span></label>
                        <input
                          name="slug"
                          type="text"
                          className="form-control"
                          value={values.slug}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Enter Slug"
                          disabled
                        />
                        <span className="text-danger d-flex text-left"><ErrorMessage name="slug" /></span>
                        <small className="text-muted">Current slug: <strong>{initialData.slug}</strong></small>
                      </div>
                    </div>

                    {/* Language Sections */}
                    {LANGUAGES.map((lang) => (
                      <div key={lang.code} className="border rounded p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                        <h6 className="mb-3">
                          <span className={`fi fi-${lang.flag} me-2`}></span>
                          {lang.name} ({lang.code.toUpperCase()})
                        </h6>

                        {/* Title */}
                        <div className="row row-sm mb-3">
                          <div className="col-md-6 form-group">
                            <label className="text-left d-flex">
                              Title ({lang.name}): <span className="requirestar">*</span>
                            </label>
                            <input
                              name={`title_${lang.code}`}
                              type="text"
                              className="form-control"
                              value={values[`title_${lang.code}`]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <span className="text-danger d-flex text-left">
                              <ErrorMessage name={`title_${lang.code}`} />
                            </span>
                          </div>

                          {/* Subject */}
                          <div className="col-md-6 form-group">
                            <label className="text-left d-flex">
                              Subject ({lang.name}): <span className="requirestar">*</span>
                            </label>
                            <input
                              name={`subject_${lang.code}`}
                              type="text"
                              className="form-control"
                              value={values[`subject_${lang.code}`]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <span className="text-danger d-flex text-left">
                              <ErrorMessage name={`subject_${lang.code}`} />
                            </span>
                          </div>
                        </div>

                        {/* Description */}
                        <div className="form-group">
                          <label className="text-left d-flex">
                            Description ({lang.name}): <span className="requirestar">*</span>
                          </label>
                          <CustomCkeditor
                            fieldname={`description_${lang.code}`}
                            setFieldValue={setFieldValue}
                            value={values[`description_${lang.code}`] || ""}
                            setFieldTouched={() => {}}
                          />
                          <span className="text-danger d-flex text-left">
                            {errors[`description_${lang.code}`] && errors[`description_${lang.code}`]}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* Buttons */}
                    <div className="mt-4">
                      <button
                        className="btn btn-main-primary signbtn mr-2"
                        type="submit"
                        disabled={submitted || isSubmitting}
                      >
                        {submitted ? "Updating..." : "Update Template"}
                      </button>
                      <button
                        className="btn ripple btn-secondary"
                        type="button"
                        onClick={() => navigate(-1)}
                        disabled={submitted || isSubmitting}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default EmailTempEdit;